import "client-only"

import { getConfigParams } from "@/lib/get-config-params"
import useGlobalParamsStore from "@/store/global-params-store"
import { Result } from "@pacote/result"
import Cookies from "js-cookie"
import { v4 as uuidV4 } from "uuid"
import {
  createFetcher,
  FetcherArgs,
  FetcherErrorResponse,
} from "./create-fetcher"

type DefaultData = {
  channel: number
  ipAddress: string
  requestId: string
  infoChannel?: string // Make this property optional
}

type DefaultHeaders = {
  "X-Call-Id": string
  "X-Session-Id": string
  "X-Channel-Id": string
  "X-Channel-Info": string
}

type ClientFetcherConfig = {
  enrichRequestData?: boolean
}

const addDefaults = (data: any = {}) => {
  const { channel } = getConfigParams()
  const { ipAddress } = useGlobalParamsStore.getState()

  const defaultData: DefaultData = {
    channel,
    ipAddress,
    requestId: uuidV4(),
  }

  if ("userAgent" in data) delete defaultData.infoChannel

  return {
    ...defaultData,
    ...data,
  }
}

const getDefaultHeaders = (): DefaultHeaders => {
  const { channel } = getConfigParams()

  return {
    "X-Call-Id": uuidV4(),
    "X-Session-Id": sessionStorage.getItem("session-id") ?? uuidV4(),
    "X-Channel-Id": channel.toString(),
    "X-Channel-Info": navigator.userAgent,
  }
}

const isVerbAllowingRequestBody = (verb?: string) => {
  return verb != null && ["POST", "PUT", "PATCH"].includes(verb)
}

export const baseClientFetcher = <S, E>(
  arg: string | FetcherArgs,
  config?: ClientFetcherConfig,
): Promise<Result<S, FetcherErrorResponse<E>>> => {
  const baseUrl = window.APP_API_URL

  if (!baseUrl) {
    throw new Error("window.APP_API_URL is not defined")
  }

  const fetchBaseQuery = createFetcher({ baseUrl })

  // The new "core" services have common data in the headers, while the legacy
  // ones have it in the request body / params. Until the migration is complete,
  // this behavior defaults to true.
  const enrichRequestData = config?.enrichRequestData ?? true
  console.log({ enrichRequestData })
  if (typeof arg === "string") {
    // arg is directly the url

    return fetchBaseQuery({
      url: arg,
      headers: getDefaultHeaders(),
      ...(enrichRequestData ? { params: addDefaults() } : {}),
    })
  } else {
    // arg is FetcherArgs

    const argsWithDefaultHeaders: FetcherArgs = {
      ...arg,
      headers: {
        ...getDefaultHeaders(),
        ...arg.headers,
      },
    }

    if (enrichRequestData) {
      return fetchBaseQuery({
        ...argsWithDefaultHeaders,
        ...(isVerbAllowingRequestBody(arg.method)
          ? { body: addDefaults(arg.body) }
          : { params: addDefaults(arg.params) }),
      })
    } else {
      return fetchBaseQuery(argsWithDefaultHeaders)
    }
  }
}

export const withAuthClientFetcher = <S, E>(
  arg: string | FetcherArgs,
  config?: ClientFetcherConfig,
): Promise<Result<S, FetcherErrorResponse<E>>> => {
  const authToken =
    useGlobalParamsStore.getState().jwt ||
    Cookies.get("pre-migration-auth-token") // To delete after the premigration period expires

  const authHeaders = {
    Authorization: `Bearer ${authToken}`,
  }

  if (typeof arg === "string") {
    return baseClientFetcher({ url: arg, headers: authHeaders }, config)
  } else {
    return baseClientFetcher(
      {
        ...arg,
        url: arg.url,
        headers: {
          ...arg.headers,
          ...authHeaders,
        },
      },
      config,
    )
  }
}

export const baseExternalFetcher = createFetcher({})
